/**
 * Food growing in the world.
 *
 * Kate asked for meat from kills, and that exists. But a plant-eater had nothing to find:
 * grazing works anywhere, which means the world gives you no reason to be in one place
 * rather than another. Berries and fish are food you have to *go to*, which is what makes
 * a map worth crossing.
 *
 * Berry bushes regrow, so a patch you found is worth remembering and coming back to.
 */
import * as THREE from 'three'
import { MAP_HALF_EXTENT, WATER } from './map'
import { heightAt, waterSurfaceHeight } from './terrain'

/** How close you must be to pick. */
const REACH = 2.4
/** Seconds for a picked bush to grow its berries back. */
const REGROW_SECONDS = 55
/** Hunger restored per pick. */
export const BERRY_FEEDS = 2
export const FISH_FEEDS = 3

interface Bush {
  x: number
  z: number
  /** Seconds until it fruits again, 0 when ripe. */
  cooldown: number
  index: number
}

/** Berry bushes: food for plant-eaters, scattered where they can be searched out. */
export class BerryPatch {
  readonly group = new THREE.Group()
  private readonly bushes: Bush[] = []
  private readonly berries: THREE.InstancedMesh
  private readonly matrix = new THREE.Matrix4()
  private readonly position = new THREE.Vector3()
  private readonly quaternion = new THREE.Quaternion()
  private readonly scale = new THREE.Vector3(1, 1, 1)

  constructor(count = 46, seed = 5150) {
    let a = seed >>> 0
    const random = (): number => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }

    // The bush itself — always present, so a picked patch is still findable.
    const leafGeometry = new THREE.SphereGeometry(0.62, 10, 8)
    const leafMaterial = new THREE.MeshStandardMaterial({ color: 0x2f5a26, roughness: 0.95 })
    const leaves = new THREE.InstancedMesh(leafGeometry, leafMaterial, count)
    leaves.castShadow = true
    leaves.receiveShadow = true

    // The fruit — hidden while the bush is regrowing.
    const berryGeometry = new THREE.SphereGeometry(0.13, 8, 6)
    const berryMaterial = new THREE.MeshStandardMaterial({
      color: 0xc0304a, roughness: 0.4, emissive: 0x3a0810, emissiveIntensity: 0.5,
    })
    this.berries = new THREE.InstancedMesh(berryGeometry, berryMaterial, count * 5)
    this.berries.castShadow = true

    const edge = MAP_HALF_EXTENT - 6
    for (let i = 0; i < count; i++) {
      let x = 0
      let z = 0
      for (let attempt = 0; attempt < 24; attempt++) {
        x = (random() * 2 - 1) * edge
        z = (random() * 2 - 1) * edge
        if (Math.hypot(x - WATER.x, z - WATER.z) > WATER.radius + 3 && Math.hypot(x, z) > 8) break
      }
      this.bushes.push({ x, z, cooldown: 0, index: i })

      this.position.set(x, heightAt(x, z) + 0.5, z)
      this.scale.set(1 + random() * 0.5, 0.85 + random() * 0.4, 1 + random() * 0.5)
      this.matrix.compose(this.position, this.quaternion, this.scale)
      leaves.setMatrixAt(i, this.matrix)
    }
    leaves.instanceMatrix.needsUpdate = true

    this.group.add(leaves, this.berries)
    this.refreshBerries()
  }

  /** Show fruit only on bushes that are ripe. */
  private refreshBerries(): void {
    this.scale.set(1, 1, 1)
    let slot = 0
    for (const bush of this.bushes) {
      const ground = heightAt(bush.x, bush.z)
      for (let b = 0; b < 5; b++) {
        const angle = (b / 5) * Math.PI * 2
        this.position.set(
          bush.x + Math.cos(angle) * 0.4,
          bush.cooldown > 0 ? -60 : ground + 0.62 + (b % 2) * 0.22,
          bush.z + Math.sin(angle) * 0.4,
        )
        this.matrix.compose(this.position, this.quaternion, this.scale)
        this.berries.setMatrixAt(slot++, this.matrix)
      }
    }
    this.berries.instanceMatrix.needsUpdate = true
  }

  get ripeCount(): number {
    return this.bushes.filter((b) => b.cooldown <= 0).length
  }

  /** Is there a ripe bush within reach of this position? */
  ripeNear(x: number, z: number): boolean {
    return this.bushes.some((b) => b.cooldown <= 0 && Math.hypot(b.x - x, b.z - z) <= REACH)
  }

  /** Pick the nearest ripe bush. Returns true if something was picked. */
  pick(x: number, z: number): boolean {
    const bush = this.bushes
      .filter((b) => b.cooldown <= 0 && Math.hypot(b.x - x, b.z - z) <= REACH)
      .sort((a, b) => Math.hypot(a.x - x, a.z - z) - Math.hypot(b.x - x, b.z - z))[0]
    if (!bush) return false
    bush.cooldown = REGROW_SECONDS
    this.refreshBerries()
    return true
  }

  /**
   * Berries ripen in the light. At night nothing regrows, so a day spent picking the
   * map clean leaves you with nothing to come back to until morning — food is worth
   * gathering while it is there rather than grazing wherever you happen to stand.
   */
  update(dt: number, daylight = 1): void {
    if (daylight <= 0.05) return
    let changed = false
    for (const bush of this.bushes) {
      if (bush.cooldown > 0) {
        bush.cooldown -= dt * daylight
        if (bush.cooldown <= 0) changed = true
      }
    }
    if (changed) this.refreshBerries()
  }
}

/** Fish in the pond — food a meat-eater has to go to the water for. */
export class FishShoal {
  readonly group = new THREE.Group()
  private readonly mesh: THREE.InstancedMesh
  private readonly phases: number[] = []
  private caught = 0
  private readonly count: number

  constructor(count = 9) {
    this.count = count
    const geometry = new THREE.ConeGeometry(0.16, 0.55, 7)
    geometry.rotateX(-Math.PI / 2)
    const material = new THREE.MeshStandardMaterial({
      color: 0x9aa86f, roughness: 0.3, metalness: 0.35,
    })
    this.mesh = new THREE.InstancedMesh(geometry, material, count)
    for (let i = 0; i < count; i++) this.phases.push((i / count) * Math.PI * 2)
    this.group.add(this.mesh)
  }

  get remaining(): number {
    return this.count - this.caught
  }

  /** Circle them slowly under the surface. */
  update(time: number): void {
    const matrix = new THREE.Matrix4()
    const position = new THREE.Vector3()
    const quaternion = new THREE.Quaternion()
    const scale = new THREE.Vector3(1, 1, 1)
    const up = new THREE.Vector3(0, 1, 0)
    const surface = waterSurfaceHeight()

    for (let i = 0; i < this.count; i++) {
      const caught = i < this.caught
      const angle = this.phases[i]! + time * 0.35
      const radius = WATER.radius * (0.3 + (i % 4) * 0.16)
      position.set(
        WATER.x + Math.cos(angle) * radius,
        caught ? -60 : surface - 0.35 + Math.sin(time + i) * 0.12,
        WATER.z + Math.sin(angle) * radius,
      )
      quaternion.setFromAxisAngle(up, -angle + Math.PI / 2)
      matrix.compose(position, quaternion, scale)
      this.mesh.setMatrixAt(i, matrix)
    }
    this.mesh.instanceMatrix.needsUpdate = true
  }

  /** Catch one, if you are actually in the water. */
  catchOne(x: number, z: number): boolean {
    if (this.caught >= this.count) return false
    if (Math.hypot(x - WATER.x, z - WATER.z) > WATER.radius) return false
    this.caught++
    return true
  }
}
